import { useMemo } from 'react';

import startCase from 'lodash/startCase';
import { BooleanField } from './_components/BooleanField';

import { DropdownField } from './_components/DropdownField';
import { useCustomOverrides } from './custom-overrides-state';
import { FieldConfig } from './types';
import { Typography, Stack, tokens } from '@cast/design-system';

const UNGROUPED = Symbol('ungrouped');

const useGroupedFields = (fields: FieldConfig[]) =>
  useMemo(() => {
    const groups = new Map<string | typeof UNGROUPED, FieldConfig[]>();
    for (const field of fields) {
      const key = field.group ?? UNGROUPED;
      const list = groups.get(key) ?? [];
      list.push(field);
      groups.set(key, list);
    }
    return groups;
  }, [fields]);

const FieldControl = ({
  field,
  value,
  onChange,
  onClear,
}: {
  field: FieldConfig;
  value: unknown;
  onChange: (value: unknown) => void;
  onClear: () => void;
}) => {
  switch (field.type) {
    case 'boolean':
      return (
        <BooleanField
          value={value as boolean | undefined}
          onChange={onChange}
          onClear={onClear}
        />
      );
    case 'dropdown':
      return (
        <DropdownField
          options={field.options}
          value={value}
          onChange={onChange}
          onClear={onClear}
        />
      );
  }
};

const FieldRow = ({
  field,
  value,
  onChange,
  onClear,
}: {
  field: FieldConfig;
  value: unknown;
  onChange: (value: unknown) => void;
  onClear: () => void;
}) => (
  <Stack direction="row" alignItems="center" gap={8}>
    <Typography variant="P12M">
      {startCase(field.name)}{' '}
      <Typography
        component="span"
        variant="P12R"
        color={tokens.theme.fg.structural.secondary}
      >
        ({field.name})
      </Typography>
    </Typography>
    <FieldControl
      field={field}
      value={value}
      onChange={onChange}
      onClear={onClear}
    />
  </Stack>
);

export const CustomOverrides = () => {
  const fields = useCustomOverrides((s) => s.fields);
  const overrides = useCustomOverrides((s) => s.overrides);
  const override = useCustomOverrides((s) => s.override);
  const clearOverride = useCustomOverrides((s) => s.clearOverride);
  const grouped = useGroupedFields(fields);

  return (
    <Stack height="100%" py={8} gap={8}>
      {[...grouped.entries()].map(([group, groupFields]) => (
        <Stack key={String(group)} gap={8}>
          {group !== UNGROUPED && (
            <Typography
              variant="L12M"
              color={tokens.theme.fg.structural.secondary}
            >
              {startCase(group)}
            </Typography>
          )}
          <Stack gap={8} pl={group !== UNGROUPED ? 12 : 0}>
            {groupFields.map((field) => (
              <FieldRow
                key={field.name}
                field={field}
                value={overrides[field.name]}
                onChange={(value) => override(field.name, value)}
                onClear={() => clearOverride(field.name)}
              />
            ))}
          </Stack>
        </Stack>
      ))}
      {fields.length === 0 && (
        <Typography variant="P12R" color={tokens.theme.fg.structural.secondary}>
          No custom overrides registered
        </Typography>
      )}
    </Stack>
  );
};
